import { useCallback, useEffect, useState } from "react";
import type { Annotation, AnnotationId } from "@lancer-vtt/domain";

export type AnnotationSelection = {
  selectedAnnotationId: AnnotationId | null;
  annotationInspectorOpen: boolean;
  selectAnnotation: (id: AnnotationId) => void;
  inspectAnnotation: (id: AnnotationId) => void;
  deselectAnnotation: () => void;
};

/**
 * ── Select annotations ──
 *
 * Local UI state only: selection is never synced (unlike the persisted stroke itself), so it
 * needs no command and no host prop. Click-select a stroke (its widened `hitStrokeWidth` on the
 * render layer is what makes a thin line catch a click at all), double-click opens the tiny
 * recolor/delete inspector, Delete/Backspace removes the selection.
 */
export function useAnnotationSelection(annotations: Record<AnnotationId, Annotation>): AnnotationSelection {
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<AnnotationId | null>(null);
  const [annotationInspectorOpen, setAnnotationInspectorOpen] = useState(false);
  const selectAnnotation = useCallback((id: AnnotationId) => {
    setSelectedAnnotationId(id);
    setAnnotationInspectorOpen(false);
  }, []);
  const inspectAnnotation = useCallback((id: AnnotationId) => {
    setSelectedAnnotationId(id);
    setAnnotationInspectorOpen(true);
  }, []);
  const deselectAnnotation = useCallback(() => {
    setSelectedAnnotationId(null);
    setAnnotationInspectorOpen(false);
  }, []);
  // A remote erase (by the owner, or the GM) can drop the currently-selected annotation out from
  // under a client that merely had it selected — settle the selection rather than leaving a dangling
  // id pointed at nothing (the inspector's own render guard already no-ops on a missing annotation;
  // this just keeps the state tidy so a stale Delete/Backspace doesn't fire on an already-gone id).
  useEffect(() => {
    if (selectedAnnotationId && !annotations[selectedAnnotationId]) deselectAnnotation();
  }, [selectedAnnotationId, annotations, deselectAnnotation]);

  return { selectedAnnotationId, annotationInspectorOpen, selectAnnotation, inspectAnnotation, deselectAnnotation };
}
